<template>
    <DataTable
        :header="$t('summary')"
        :fields="fields"
        :items="items"
        :loading="loading"
        :error="request.getError()"
        class="mb-4"
        show-export-button
    >
        <template #headerSide>
            <div :key="spaceId" class="d-flex gap-3">
                <MultiselectAvailableCampaigns :disabled="loading" @changeTrackBy="campaigns = $event" @loading="campaignsLoading = $event" />
                <FormCategoryTreeSelect
                    ref="FormCategoryTreeSelect"
                    :value="categories"
                    :disabled="loading"
                    class="width-200"
                    preselect
                    prevent-empty-selection
                    :clearable="false"
                    @change="categories = $event"
                    @loading="categoriesLoading = $event"
                />
            </div>
        </template>
        <template #cell(impressions)="{item}">
            <div>{{ item.impressions }}</div>
            <div><Difference :difference="$format.percent(item.impressionsDiff)" /></div>
        </template>
        <template #cell(clicks)="{item}">
            <div>{{ item.clicks }}</div>
            <div><Difference :difference="$format.percent(item.clicksDiff)" /></div>
        </template>
        <template #cell(cr)="{item}">
            <div>{{ $format.percent(item.cr) }}</div>
            <div><Difference :difference="$format.percent(item.crDiff)" /></div>
        </template>
    </DataTable>
</template>



<script>
    import sumBy from 'lodash/sumBy';
    import { calcPercentDiff } from '@/utils/number';
    import DataTable from '@/components/common/DataTable.vue';
    import Difference from '@/components/common/Difference.vue';
    import MultiselectAvailableCampaigns from '@/components/common/MultiselectAvailableCampaigns.vue';
    import FormCategoryTreeSelect from '@/components/common/FormCategoryTreeSelect.vue';

    export default {
        name: 'CampaignReport',
        components: {
            DataTable,
            Difference,
            MultiselectAvailableCampaigns,
            FormCategoryTreeSelect,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                request: this.$req('space/CampaignReports'),
                campaigns: [],
                categories: [],
                campaignsLoading: false,
                categoriesLoading: false,
            };
        },
        computed: {
            fields() {
                return [
                    {
                        key: 'name',
                        label: this.$t('campaign'),
                        sortable: true,
                        exportable: true,
                    },
                    {
                        key: 'impressions',
                        label: this.$t('impressions'),
                        sortable: true,
                        exportable: true,
                        total: true,
                        formatter: val => this.$format.number(val),
                    },
                    {
                        key: 'impressionsDiff',
                        label: `${this.$t('impressions')} (% ${this.$t('change2').toLowerCase()})`,
                        sortable: true,
                        exportable: true,
                        show: false,
                        formatter: val => this.$format.number(val),
                    },
                    {
                        key: 'clicks',
                        label: this.$t('clicks'),
                        sortable: true,
                        exportable: true,
                        total: true,
                        formatter: val => this.$format.number(val),
                    },
                    {
                        key: 'clicksDiff',
                        label: `${this.$t('clicks')} (% ${this.$t('change2').toLowerCase()})`,
                        sortable: true,
                        exportable: true,
                        show: false,
                        formatter: val => this.$format.number(val),
                    },
                    {
                        key: 'quantity',
                        label: this.$t('quantity2'),
                        sortable: true,
                        exportable: true,
                        show: false,
                        formatter: val => this.$format.number(val),
                    },
                    {
                        key: 'quantityDiff',
                        label: `${this.$t('quantity2')} (% ${this.$t('change2').toLowerCase()})`,
                        sortable: true,
                        exportable: true,
                        show: false,
                        formatter: val => this.$format.number(val),
                    },
                    {
                        key: 'amount',
                        label: `${this.$t('amount')} [${process.env.VUE_APP_SYSTEM_CURRENCY}]`,
                        sortable: true,
                        exportable: true,
                        formatter: val => this.$format.number(val),
                        show: false,
                    },
                    {
                        key: 'amountDiff',
                        label: `${this.$t('amount')} (% ${this.$t('change2').toLowerCase()})`,
                        sortable: true,
                        exportable: true,
                        show: false,
                        formatter: val => this.$format.number(val),
                    },
                    {
                        key: 'cr',
                        label: this.$t('cr'),
                        sortable: true,
                        exportable: true,
                        total: true,
                        formatter: val => this.$format.number(val),
                        totalFormatter: val => this.$format.percent(val),
                        totalValue: (item, tableItems) => (sumBy(tableItems, 'quantity') / sumBy(tableItems, 'clicks')) * 100 || 0,
                    },
                    {
                        key: 'crDiff',
                        label: `${this.$t('cr')} (% ${this.$t('change2').toLowerCase()})`,
                        sortable: true,
                        exportable: true,
                        show: false,
                        formatter: val => this.$format.number(val),
                    },
                ];
            },
            items() {
                return (this.request.getData() || []).map(item => ({
                    ...item,
                    cr: (item.quantity / item.clicks) * 100 || 0,
                    crDiff: calcPercentDiff((item.quantity / item.clicks) * 100, (item.quantity_compare / item.clicks_compare) * 100),
                    impressionsDiff: calcPercentDiff(item.impressions, item.compareImpressions),
                    clicksDiff: calcPercentDiff(item.clicks, item.compareClicks),
                    quantityDiff: calcPercentDiff(item.quantity, item.compareQuantity),
                    amountDiff: calcPercentDiff(item.amount, item.compareAmount),
                }));
            },
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            timeRange() {
                return this.parentComponent.timeRange;
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const { startDate, endDate, compareStartDate, compareEndDate } = this.timeRange;
                const requestParams = { startDate, endDate, compareStartDate, compareEndDate };
                if (this.campaigns?.length) requestParams.campaigns = this.campaigns.join(',');
                if (this.categories?.length) requestParams.categories = this.categories.join(',');
                return { urlParams, requestParams };
            },
            loadingFilters() {
                return this.campaignsLoading || this.categoriesLoading;
            },
            loading() {
                return this.request.getLoading() || this.loadingFilters;
            },
        },
        watch: {
            spaceId() {
                this.resetFilters();
            },
            apiParams: {
                immediate: true,
                handler(newVal, oldVal) {
                    if (JSON.stringify(newVal || {}) !== JSON.stringify(oldVal || {})) {
                        this.loadData();
                    }
                },
            },
        },
        methods: {
            calcPercentDiff,
            loadData() {
                if (!this.loadingFilters) {
                    this.resetData();
                    this.fetchData();
                }
            },
            resetData() {
                this.request.abortAll();
            },
            fetchData() {
                this.request.get(this.apiParams);
            },
            resetFilters() {
                this.campaigns = [];
                this.categories = [];
            },
        },
    };
</script>
